import React, { useState, useEffect } from 'react';
import { useRouteData, useSiteData, Head } from 'react-static';
// template components
import Page from '@nmx/template/dist/componentLibrary/layout/Page';
import ContentHub from '@nmx/template/dist/componentLibrary/modules/ContentHub';
import InPageLeadForm from '@nmx/template/dist/componentLibrary/modules/InPageLeadForm';
import Theme from '@nmx/template/dist/componentLibrary/Theme';
// native components
import AdvisorConnectModule from './components/advisorConnectModule';
import CredentialCalloutGrouping from './components/credentialCalloutGrouping';
import ExperienceCallout from './components/experienceCallout';
import Faq from './components/faq';
import HeroSection from './components/heroSection';
import InPageLeadFormLazyLoadContainer from '../../components/inPageLeadFormLazyLoadContainer';
import IntroSection from './components/introSection';
import Metadata from './components/metadata';
import QuizCallout from './components/quizCallout';
import UnderstandingProduct from './components/understandingProduct';
// images for prefetch
import highResDesktopImageWebp from './components/heroSection/images/term-life-insurance-hero.webp';
import highResMobileImageWebp from './components/heroSection/images/term-life-insurance-hero-mobile.webp';
// dislaimer footnotes
import {
  HighestFinancialStrengthRatings,
  LoyaltyBasedOnClientData,
} from '../../../utils/disclaimerFootnotes';
// other utils
import { getImmediateMatchFeatureFlag } from '../../../utils/getFeatureFlags/getFeatureFlags';

export default () => {
  const { config, dynamicConfig } = useSiteData();
  const { pageProps, contentHubItems } = useRouteData();
  // We don't want to load recaptcha on page load (for performance reasons) so we will load it once the in page lead for is engaged with
  const [loadRecaptcha, setLoadRecaptcha] = useState(false);
  const [immediateMatchFeatureFlag, setImmediateMatchFeatureFlag] = useState(false);

  useEffect(() => {
    getImmediateMatchFeatureFlag(setImmediateMatchFeatureFlag, config);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page
      {...pageProps} // defined in route.js
      headComponent={Head}
      config={config}
      dynamicConfig={dynamicConfig}
      useReCaptcha={loadRecaptcha}
      disableDisclaimerTopBorder
      breadcrumbsNavigationConfig={[
        {
          pageName: 'Home',
          linkUrl: '/',
          linkAriaLabel: 'Northwestern Mutual Home',
        },
        {
          pageName: 'Insurance',
          linkUrl: '/insurance/',
          linkAriaLabel: 'Northwestern Mutual Insurance',
        },
        {
          pageName: 'Life Insurance',
          linkUrl: '/life-insurance/',
          linkAriaLabel: 'Northwestern Mutual Life Insurance',
        },
        {
          pageName: 'Term Life Insurance',
          linkUrl: '/life-insurance/term-life-insurance/',
          linkAriaLabel: 'Northwestern Mutual Term Life Insurance',
        },
      ]}
      disclaimerFootnotes={[
        <>
          <sup>1</sup>&nbsp;Based on a healthy male in the premier class, age 35, purchasing Northwestern Mutual&#39;s Term to Age 80 for &#36;500,000 with a premium starting at &#36;306 per year &#40;increasing annually&#41;. Rates vary based on age, gender, medical history, and death benefit amount.
        </>,
        <>
          <sup>2</sup>&nbsp;Unless you cancel or stop paying your premiums. Utilizing the accumulated value through policy loans, surrenders, or cash withdrawals will reduce the death benefit; and may necessitate greater outlay than anticipated and&#47;or result in an unexpected taxable event. Assumes a non&#45;Modified Endowment Contract (MEC).
        </>,
        <>
          {LoyaltyBasedOnClientData(3)}
        </>,
        <>
          <sup>4</sup>&nbsp;Latest U.S. rank as of 2019 based on direct premiums written. Source: S&#38;P Global Market Intelligence. Prepared calculated by Northwestern Mutual.
        </>,
        <>
          {HighestFinancialStrengthRatings(5)}
        </>,
      ]}>
      <Head>
        {/* prefetch hero images */}
        <link rel='preload' href={highResMobileImageWebp} as='image' media={`(max-width: ${Theme.breakpoints.values.medium}px)`} />
        <link rel='preload' href={highResDesktopImageWebp} as='image' media={`(min-width: ${Theme.breakpoints.values.medium + 1}px)`} />
      </Head>

      <Metadata
        image={highResDesktopImageWebp}
        pageDescription={pageProps.pageDescription}
        pageTitle={pageProps.pageTitle}
      />

      <HeroSection />

      <IntroSection />

      <UnderstandingProduct />

      <ExperienceCallout />

      <Faq />

      <InPageLeadFormLazyLoadContainer>
        <InPageLeadForm
          sectionId='in-page-lead' // note: id in-page-lead-form will be on the Form element itself.
          config={config}
          formLocation='term-life-insurance'
          immediateMatchFeatureFlagEnabled={immediateMatchFeatureFlag}
          onFormEngaged={() => {
            if (!loadRecaptcha) {
              setLoadRecaptcha(true);
            }
          }}
          variant='embedded'
        />
      </InPageLeadFormLazyLoadContainer>

      <CredentialCalloutGrouping />

      <QuizCallout />

      <ContentHub
        sectionId='content-hub'
        headingText='Boost your term life insurance knowledge'
        contentHubItems={contentHubItems} // defined in route.js
        config={config}
      />

      <AdvisorConnectModule />

    </Page>
  );
};

